import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react'
import {
  useRecoilValue,
  useSetRecoilState,
  useRecoilState,
  useRecoilCallback,
} from 'recoil'
import { useNudgeItem } from 'hooks'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useEditorOutsideClick } from 'hooks/editor/useEditorOutsideClick'
import { useItemControlState } from '@/hooks/editor/useItemControlState'
import {
  itemDraggingAtom,
  edgeResizePreviewAtomFamily,
} from '@/atoms/itemControlAtoms'
import {
  alignmentPointsAtom,
  alignmentVisibilityAtom,
  revealWrapperHeightAtom,
  groupDragOffsetSelector,
  isItemSelectedSelector,
  isSoleSelectionSelector,
  marqueeCoversItemSelector,
  pagePaddingJsonSelector,
} from '@/atoms/_editorAtoms'
import {
  pageZoomAtom,
  itemSelector,
  selectedPageAtom,
  controlledXAtom,
  controlledYAtom,
  rightMenuAtom,
  snapToggleAtom,
  selectedItemsAtom,
} from 'atoms'
import Draggable from 'react-draggable'
import { ResizeControls } from 'components'
import { ClassicResizeControls } from './ClassicResizeControls'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'
import {
  EDITOR_TEXT_SCALING_ENABLED,
  editorTextBoxFrameFollowsText,
  isEditorItem,
  isEditorTextBoxItem,
  scaleEditorTextBoxDocumentSizes,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { canvasPageIdAtom, unsavedChangesAtom } from '@/atoms/_editorAtoms'
import {
  editorItemLockedSelector,
  editorTextBoxDocumentsSelector,
  editorTextBoxDraftsAtom,
} from '@/atoms/editorTextBoxAtoms'
import { useSnapToGuides } from '@/hooks/editor/useSnapToGuides'
import * as Items from './'
import * as S from './Item.styled'
import { roleAtom } from 'atoms'
import transformToPascalCase from 'utils/global/transformToPascalCase'
import {
  multiSelectModeAtom,
  commitMultiSelectPositionsAtom,
} from '@/atoms/_editorAtoms'

export const Item = ({ type, id, hasMultiSelected, environment, ...props }) => {
  countEditorProbeRender('Item')
  useEditorProbeLifetime('Item')
  //refs
  const item = useRef(null)
  const nodeRef = useRef(null)

  const thisItem = useRecoilValue(itemSelector({ atomFamily: type, id: id }))
  const { x, y, width, height, z, assetLocked, maintainAspectRatio } = useMemo(
    () => thisItem,
    [thisItem],
  )

  // Memoize width/height calculations
  const convertedWidth = useMemo(
    () => (typeof width === 'string' ? parseInt(width) : width),
    [width],
  )

  const convertedHeight = useMemo(
    () => (typeof height === 'string' ? parseInt(height) : height),
    [height],
  )

  //local state
  const [resizingState, setResizingState] = useState('idle')
  const [resizeHandle, setResizeHandle] = useState(null)
  const [dragDisabled, setDragDisabled] = useState(false)
  const [internalWidth, setInternalWidth] = useState(convertedWidth)
  const [internalHeight, setInternalHeightState] = useState(convertedHeight)
  // The atom is the size's source of truth between gestures; reconciling it
  // during render (not in an effect) means a hydrated size paints in the
  // same frame it arrives and never depends on an effect being scheduled.
  const [syncedSize, setSyncedSize] = useState({
    width: convertedWidth,
    height: convertedHeight,
  })
  if (
    !Object.is(syncedSize.width, convertedWidth) ||
    !Object.is(syncedSize.height, convertedHeight)
  ) {
    countEditorProbeRender(
      `Item.syncInternal:${String(id).slice(0, 8)}:h=${convertedHeight}`,
    )
    setSyncedSize({ width: convertedWidth, height: convertedHeight })
    setInternalWidth(convertedWidth)
    setInternalHeightState(convertedHeight)
  }
  const itemExists = convertedHeight > 0 || convertedWidth > 0
  const setInternalHeight = useCallback(
    (next) => {
      countEditorProbeRender(
        `Item.setInternalHeight:${String(id).slice(0, 8)}:${typeof next === 'function' ? 'fn' : next}`,
      )
      setInternalHeightState(next)
    },
    [id],
  )
  countEditorProbeRender(
    `Item.render:${String(id).slice(0, 8)}:atomH=${height}:internal=${internalHeight}:type=${thisItem.type}:ch=${convertedHeight}:cw=${convertedWidth}:hType=${typeof height}`,
  )
  useLayoutEffect(() => {
    countEditorProbeRender(
      `Item.commit:${String(id).slice(0, 8)}:atomH=${height}:internal=${internalHeight}`,
    )
  })
  const [controlledX, setControlledX] = useState(x)
  const [controlledY, setControlledY] = useState(y)
  const groupDragAccum = useRef({ x: 0, y: 0 })
  const groupDragBase = useRef(null)
  const [isEditing, setIsEditing] = useState(false)
  const [convertedToEditable, setConvertedToEditable] = useState(false)
  const [pendingDeselect, setPendingDeselect] = useState(false)

  //recoil state
  const userRole = useRecoilValue(roleAtom)
  const revealWrapperHeight = useRecoilValue(revealWrapperHeightAtom)
  const activeMenu = useRecoilValue(rightMenuAtom)
  const snapToggle = useRecoilValue(snapToggleAtom)
  // The page's lock setting for a classic item, the box's own session lock
  // for an editor text box.
  const itemLocked = useRecoilValue(editorItemLockedSelector(id))
  const zoom = useRecoilValue(pageZoomAtom)
  const selectedPage = useRecoilValue(selectedPageAtom)
  const alignmentPoints = useRecoilValue(alignmentPointsAtom)
  const marqueeCoversItem = useRecoilValue(marqueeCoversItemSelector(id))
  const multiSelectMode = useRecoilValue(multiSelectModeAtom)
  const paddingJson = useRecoilValue(pagePaddingJsonSelector(selectedPage - 1))
  const padding = useMemo(() => JSON.parse(paddingJson), [paddingJson])

  const setSelectedItems = useSetRecoilState(selectedItemsAtom)
  const itemSelected = useRecoilValue(isItemSelectedSelector(id))
  const selected = useRecoilValue(isSoleSelectionSelector(id))
  const edgeResizePreview = useRecoilValue(edgeResizePreviewAtomFamily(id))
  const [commitMultiSelectPositions, setCommitMultiSelectPositions] =
    useRecoilState(commitMultiSelectPositionsAtom)

  const setGlobalControlledX = useSetRecoilState(controlledXAtom)
  const setGlobalControlledY = useSetRecoilState(controlledYAtom)
  const setAlignmentVisibility = useSetRecoilState(alignmentVisibilityAtom)
  const setThisItemX = useSetRecoilState(itemSelector({ id: id, path: 'x' }))
  const setThisItemY = useSetRecoilState(itemSelector({ id: id, path: 'y' }))
  const setThisItemHeight = useSetRecoilState(
    itemSelector({
      atomFamily: type,
      id: id,
      path: 'height',
      annotation: true,
    }),
  )
  const setThisItemWidth = useSetRecoilState(
    itemSelector({
      atomFamily: type,
      id: id,
      path: 'width',
      annotation: true,
    }),
  )

  const setItemDragging = useSetRecoilState(itemDraggingAtom)

  // Asset lock check - must be before isItemSelectable so locked items can't be group selected
  const isAdmin = useIsAdmin()
  const isAssetLocked = useMemo(
    () => !!assetLocked && !isAdmin,
    [assetLocked, isAdmin],
  )

  // Asset locked items should never be selectable via group selection
  const isItemSelectable = marqueeCoversItem && !dragDisabled && !isAssetLocked

  const internalWidthState = useMemo(
    () => [internalWidth, setInternalWidth],
    [internalWidth],
  )
  const internalHeightState = useMemo(
    () => [internalHeight, setInternalHeight],
    [internalHeight, setInternalHeight],
  )
  const dragDisabledState = useMemo(
    () => [isEditing, setIsEditing],
    [isEditing],
  )
  const convertedToEditableStateMemo = useMemo(
    () => [convertedToEditable, setConvertedToEditable],
    [convertedToEditable],
  )

  const isItemSelected = useMemo(
    () => isItemSelectable || itemSelected,
    [isItemSelectable, itemSelected],
  )

  const groupDragOffset = useRecoilValue(groupDragOffsetSelector(id))

  // Use snap-to-guides hook for all snap behavior
  const { handleSnapDrag, handleSnapStop } = useSnapToGuides({
    item: thisItem,
    padding,
    alignmentPoints,
    snapToggle,
    controlledX,
    controlledY,
    setControlledX,
    setControlledY,
    setAlignmentVisibility,
  })

  useEffect(() => {
    if (Number.isFinite(x) && !Number.isFinite(controlledX)) setControlledX(x)
    if (Number.isFinite(y) && !Number.isFinite(controlledY)) setControlledY(y)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y])

  // Sync internal dimensions from edge resize preview during drag
  useEffect(() => {
    if (edgeResizePreview) {
      countEditorProbeRender(`Item.edgePreview:h=${edgeResizePreview.height}`)
      setInternalWidth(edgeResizePreview.width)
      setInternalHeightState(edgeResizePreview.height)
    }
  }, [edgeResizePreview])

  useEffect(() => {
    if (!isItemSelectable) return
    setSelectedItems((prev) =>
      prev.includes(thisItem.id) ? prev : [...prev, thisItem.id],
    )
  }, [isItemSelectable, thisItem.id, setSelectedItems])

  useEffect(() => {
    //we use the active menu to determine which items are selectable
    //if looking at Elements then dragDisabled is true for watermarks
    //if looking at Watermarks then dragDisabled is true for NOT watermarks
    //if looking at Background nothing is interactive
    if (activeMenu === 'Elements' || activeMenu === 'Settings') {
      setDragDisabled(thisItem.type === 'watermark')
    } else if (activeMenu === 'Watermarks') {
      setDragDisabled(thisItem.type !== 'watermark')
    } else if (activeMenu === 'Background') {
      setDragDisabled(true)
      setSelectedItems([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisItem.type, activeMenu])

  useEditorOutsideClick(item, nodeRef, itemSelected, 'click', {
    panelsBlurSelection: isEditorTextBoxItem(thisItem),
    tolerateClickJitter: isEditorItem(thisItem),
  })

  const editorTextBoxMountApiRef = useRef(null)
  // The height a selected text box's text needs at its painted sizes, as its
  // read-only fit last measured it; while the box is being edited the host
  // measures instead. The resize frame reads it at every pointer move. While
  // a face that paints the box has failed, the paint is its fallback's, so
  // the text needs nothing of the frame and the handle sets it alone.
  const editorTextBoxTextNeedRef = useRef(null)
  const readEditorTextBoxTextNeedPx = useCallback(() => {
    if (nodeRef.current && !editorTextBoxFrameFollowsText(nodeRef.current))
      return null
    return (
      editorTextBoxMountApiRef.current?.paintedHeightPx() ??
      editorTextBoxTextNeedRef.current
    )
  }, [])
  // A corner scale on a merely selected box bakes its factor into the draft's
  // authored sizes; an editing box bakes inside its own mount on release.
  // With text scaling off a corner resizes the frame alone and bakes nothing.
  const onEditorTextBoxCornerScale = useRecoilCallback(
    ({ snapshot, set }) =>
      async (scale) => {
        if (!EDITOR_TEXT_SCALING_ENABLED) return
        if (editorTextBoxMountApiRef.current) return
        if (!Number.isFinite(scale) || scale <= 0) return
        if (Math.abs(scale - 1) < 1e-3) return
        const pageId = await snapshot.getPromise(canvasPageIdAtom)
        if (!pageId) return
        const drafts = await snapshot.getPromise(editorTextBoxDraftsAtom)
        const results = await snapshot.getPromise(
          editorTextBoxDocumentsSelector(pageId),
        )
        const current = drafts[id] ?? results?.get(id)?.document ?? null
        if (!current) return
        const scaled = scaleEditorTextBoxDocumentSizes(current, scale)
        if (scaled === current) return
        set(editorTextBoxDraftsAtom, (prev) => ({ ...prev, [id]: scaled }))
        set(unsavedChangesAtom, true)
      },
    [id],
  )
  // Memoize component lookup to prevent re-renders
  const ItemComponent = useMemo(
    () =>
      Items[`Page${transformToPascalCase(thisItem.type)}`] || Items.PageShape,
    [thisItem.type],
  )

  // Handle drag stop - commit position changes or handle pending deselection
  const handleStop = useCallback(() => {
    if (!nodeRef.current) return

    const wasDragging = nodeRef.current.isDragging
    nodeRef.current.isDragging = false
    setItemDragging(false)

    // Handle pending deselection (only if user clicked without dragging)
    const shouldProcessDeselection = pendingDeselect && !wasDragging
    if (shouldProcessDeselection) {
      if (multiSelectMode) {
        // Remove this item from the multi-selection
        setSelectedItems((prev) => prev.filter((id) => id !== thisItem.id))
      } else {
        // Isolate this item as the only selected item
        setSelectedItems([thisItem.id])
      }

      setPendingDeselect(false)
      return // Early return - don't commit positions when handling deselection
    }

    if (!wasDragging) {
      setGlobalControlledX(null)
      setGlobalControlledY(null)
      setPendingDeselect(false)
      return
    }

    // Handle snap-on-release and state management
    // Note: We call handleSnapStop even if not applying release snap, because it manages
    // the snap state (checking if still in zone, resetting if exited)
    const { finalX, finalY } = handleSnapStop(controlledX, controlledY)

    // Update local controlled position if we snapped
    if (finalX !== controlledX) {
      setControlledX(finalX)
    }
    if (finalY !== controlledY) {
      setControlledY(finalY)
    }

    // Commit position changes to Recoil state (persisting the drag)
    setThisItemX(finalX)
    setThisItemY(finalY)

    // Clear alignment guides
    setAlignmentVisibility({
      verticalSnap: null,
      horizontalSnap: null,
      verticalVisible: [],
      horizontalVisible: [],
    })

    // Trigger multi-select position commit for other selected items
    if (hasMultiSelected) {
      setCommitMultiSelectPositions((prev) => prev + 1)
    }

    // Reset global controlled position deltas
    setGlobalControlledX(null)
    setGlobalControlledY(null)

    setPendingDeselect(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodeRef,
    controlledX,
    controlledY,
    hasMultiSelected,
    pendingDeselect,
    multiSelectMode,
    thisItem.id,
    snapToggle,
    handleSnapStop,
    setItemDragging,
  ])

  useEffect(() => {
    setGlobalControlledX(null)
    setGlobalControlledY(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  useEffect(() => {
    if (groupDragOffset === null) {
      groupDragBase.current = null
      return
    }
    if (groupDragBase.current === null) {
      groupDragBase.current = { x: controlledX, y: controlledY }
    }
    if (groupDragOffset.x !== null) {
      setControlledX(groupDragBase.current.x + groupDragOffset.x)
    }
    if (groupDragOffset.y !== null) {
      setControlledY(groupDragBase.current.y + groupDragOffset.y)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDragOffset])

  useEffect(() => {
    // When multi-select drag completes, commit ALL selected items' positions
    if (commitMultiSelectPositions > 0 && itemSelected) {
      setThisItemX(controlledX)
      setThisItemY(controlledY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitMultiSelectPositions])

  const handleDrag = useCallback(
    (_, dragElement) => {
      if (!nodeRef.current) return

      nodeRef.current.isDragging = true
      setItemDragging(true)

      if (pendingDeselect) {
        setPendingDeselect(false)
      }

      // For multi-select, use original behavior (no snap-to-guides)
      if (hasMultiSelected) {
        groupDragAccum.current.x += dragElement.deltaX
        groupDragAccum.current.y += dragElement.deltaY
        setGlobalControlledX(groupDragAccum.current.x)
        setGlobalControlledY(groupDragAccum.current.y)
        setAlignmentVisibility({
          verticalSnap: null,
          horizontalSnap: null,
          verticalVisible: [],
          horizontalVisible: [],
        })
        return
      }

      // Handle snap-to-guides (freeze, animation, constraints)
      handleSnapDrag(dragElement)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nodeRef,
      hasMultiSelected,
      pendingDeselect,
      handleSnapDrag,
      setItemDragging,
    ],
  )

  useEffect(() => {
    if (userRole === 'loading') return
    setDragDisabled(isAssetLocked)
  }, [isAssetLocked, userRole])

  useNudgeItem({
    isItemSelected,
    dragDisabled,
    isEditing,
    // A locked editor text box cannot move, by arrow key included; a classic
    // item's nudge follows no lock.
    locked: isEditorTextBoxItem(thisItem) && itemLocked,
    controlledX,
    controlledY,
    setControlledX,
    setControlledY,
    setThisItemX,
    setThisItemY,
  })

  const commitResizePosition = useCallback(
    (newX, newY) => {
      setThisItemX(newX)
      setThisItemY(newY)
    },
    [setThisItemX, setThisItemY],
  )
  // A text box's top moves with the frame the fit that follows a release
  // raises under a north handle: the live position and the item's own.
  const shiftEditorTextBoxY = useCallback(
    (dy) => {
      if (!Number.isFinite(dy) || dy === 0) return
      setControlledY((prev) => (Number.isFinite(prev) ? prev + dy : prev))
      setThisItemY((prev) => (Number.isFinite(prev) ? prev + dy : prev))
    },
    [setThisItemY],
  )

  // Register item control state with the overlay
  // This allows the ItemControlOverlay to render controls above all items
  const itemControlState = useMemo(
    () => ({
      type: thisItem.type,
      hideInPdf: thisItem.hideInPdf,
      position: {
        x: controlledX,
        y: controlledY,
        width: internalWidth,
        height: internalHeight,
        z,
        rotate: thisItem?.edits?.rotate || 0,
      },
      selection: {
        selected: itemSelected,
        isEditing,
        disabled: dragDisabled,
        assetLocked: isAssetLocked,
      },
      text: thisItem.type === 'text' ? { convertedToEditable } : undefined,
    }),
    [
      controlledX,
      controlledY,
      internalWidth,
      internalHeight,
      z,
      thisItem.type,
      thisItem.hideInPdf,
      thisItem?.edits?.rotate,
      itemSelected,
      isEditing,
      dragDisabled,
      isAssetLocked,
      convertedToEditable,
    ],
  )

  useItemControlState(thisItem.id, itemControlState)

  // Handle mouse down for selection (supports both single and multi-select modes)
  const handleMouseDown = useCallback(() => {
    if (!nodeRef.current) return

    nodeRef.current.isDragging = false

    const isInteractionDisabled = dragDisabled || isAssetLocked
    if (isInteractionDisabled) return

    groupDragAccum.current = { x: 0, y: 0 }

    const isPartOfMultiSelection = hasMultiSelected && itemSelected

    if (multiSelectMode) {
      // Multi-select mode (Cmd/Ctrl held): toggle items in/out of selection
      if (itemSelected) {
        // Item is already selected - mark for removal on mouseup (if not dragged)
        setPendingDeselect(true)
      } else {
        // Item not selected - add it to the selection
        setPendingDeselect(false)
        setSelectedItems((prev) => [...prev, thisItem.id])
      }
    } else {
      // Single-select mode: replace selection with this item (unless clicking within existing multi-selection)
      if (isPartOfMultiSelection) {
        // Clicking within multi-selection - will isolate this item on mouseup (if not dragged)
        setPendingDeselect(true)
      } else {
        setPendingDeselect(false)

        if (!selected) {
          // Replace current selection with this item
          setSelectedItems([thisItem.id])
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodeRef,
    dragDisabled,
    thisItem.id,
    isAssetLocked,
    multiSelectMode,
    hasMultiSelected,
    selected,
    itemSelected,
  ])

  if (!itemExists) return null

  // An editor text box or rule resizes through the editor frame (corner
  // scale, anchored west/north handles, pointer travel scaled by zoom); a
  // classic item keeps the staging frame, one south-east handle and none of
  // that, so its gesture is the one TinyMCE items always had.
  const editorItem = isEditorItem(thisItem)
  const ResizeFrame = editorItem ? ResizeControls : ClassicResizeControls
  const editorResizeProps = editorItem
    ? {
        controlledX: [controlledX, setControlledX],
        controlledY: [controlledY, setControlledY],
        commitPosition: commitResizePosition,
        onResizeHandleChange: setResizeHandle,
        onEditorTextBoxCornerScale: isEditorTextBoxItem(thisItem)
          ? onEditorTextBoxCornerScale
          : undefined,
        textNeedPx: isEditorTextBoxItem(thisItem)
          ? readEditorTextBoxTextNeedPx
          : undefined,
      }
    : {}

  return (
    // An editor text box stays draggable while it is being edited: the cancel
    // selector keeps a press inside the box placing the caret instead of
    // starting a drag, and the overlay's drag handle still moves the box. The
    // toolbar's popovers and flyouts are portaled to the body, but their
    // presses still reach this handler through React's tree, so they are
    // named here too: a drag inside a colour picker moves the picker's dot,
    // never the box. Its own lock disables the drag outright, as the page's
    // lock setting does for a classic item.
    <Draggable
      nodeRef={nodeRef}
      axis="none"
      cancel={
        isEditing
          ? '.no-drag, [data-editor-text-box], [data-editor-text-toolbar-strip], [data-editor-text-popover], [data-editor-text-flyout], [data-editor-text-modal-backdrop]'
          : '.no-drag'
      }
      scale={zoom * 0.01}
      onDrag={handleDrag}
      onStop={handleStop}
      disabled={
        dragDisabled ||
        (isEditing && !isEditorTextBoxItem(thisItem)) ||
        itemLocked ||
        isAssetLocked
      }
      onMouseDown={handleMouseDown}
      position={{ x: controlledX, y: controlledY }}
    >
      <S.ItemContainer
        ref={nodeRef}
        z={dragDisabled && !isAssetLocked ? -1 : z}
        isWatermark={thisItem.type === 'watermark'}
        disabled={dragDisabled || isAssetLocked}
        data-item-id={thisItem.id}
        data-item-selected={itemSelected}
      >
        {/* This is for testing purposes on behalf of autosizing hook */}
        {revealWrapperHeight && selected && (
          <S.WrapperHeightIndicator width={width} height={height} />
        )}
        <ResizeFrame
          z={z}
          rotate={thisItem?.edits?.rotate}
          selected={itemSelected}
          internalWidth={[internalWidth, setInternalWidth]}
          internalHeight={[internalHeight, setInternalHeight]}
          disabled={dragDisabled}
          resizingState={[resizingState, setResizingState]}
          convertedToEditable={convertedToEditable}
          {...thisItem}
          {...editorResizeProps}
        >
          <ItemComponent
            full
            itemRef={item}
            selected={itemSelected}
            internalWidth={internalWidthState}
            internalHeight={internalHeightState}
            setItemHeight={setThisItemHeight}
            setItemWidth={setThisItemWidth}
            dragDisabled={dragDisabledState}
            convertedToEditableState={convertedToEditableStateMemo}
            type={type}
            maintainAspectRatio={maintainAspectRatio}
            assetLocked={isAssetLocked}
            itemLocked={itemLocked}
            resizingState={resizingState}
            resizeHandle={resizeHandle}
            environment={environment}
            controlledX={controlledX}
            controlledY={controlledY}
            multiSelectMode={multiSelectMode}
            {...(isEditorTextBoxItem(thisItem)
              ? {
                  editorTextBoxMountApiRef,
                  editorTextBoxTextNeedRef,
                  shiftItemY: shiftEditorTextBoxY,
                }
              : {})}
            {...thisItem}
            {...props}
          />
        </ResizeFrame>
      </S.ItemContainer>
    </Draggable>
  )
}

export default React.memo(Item)
